import React from 'react';

export const Terms: React.FC = () => (
  <div className="min-h-screen bg-white">
    <div className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
      <p className="text-sm text-gray-500 mb-10">Effective Date: February 24, 2026 · Last Updated: February 24, 2026</p>

      <div className="prose prose-gray max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Acceptance of Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            By accessing or using the Intrlock platform at intrlock.io (the "Service"), you agree to be
            bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
            These Terms apply to all users, including property managers, integrators, tenants, and visitors.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Description of Service</h2>
          <p className="text-gray-700 leading-relaxed">
            Intrlock provides a cloud-based platform for property access control, visitor management,
            and video intercom communication. Features include:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
            <li>Visitor directory and video intercom calling</li>
            <li>Remote door lock/unlock management</li>
            <li>Property, unit, and tenant management</li>
            <li>Custom signage design and ordering (Sign Shop)</li>
            <li>Integrator and dealer management tools</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. Accounts & Registration</h2>
          <p className="text-gray-700 leading-relaxed">
            You must provide accurate and complete information when creating an account. You are
            responsible for maintaining the confidentiality of your login credentials and for all
            activities under your account. You must notify us immediately of any unauthorized use.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Subscriptions & Billing</h2>
          <p className="text-gray-700 leading-relaxed">
            Intrlock subscriptions are billed monthly on a per-property basis. Pricing consists of a
            base fee per property plus per-unit and per-device charges. All prices are in US dollars.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            Payment is processed securely through Stripe. We do not store credit card numbers on our
            servers. Subscriptions renew automatically each month. You may cancel at any time; service
            will continue through the end of the current billing period.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            We reserve the right to change pricing with 30 days’ written notice. Continued use after
            a price change constitutes acceptance of the new pricing.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Acceptable Use</h2>
          <p className="text-gray-700 leading-relaxed">You agree not to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
            <li>Use the Service for any unlawful purpose or in violation of any laws</li>
            <li>Attempt to gain unauthorized access to any part of the Service</li>
            <li>Interfere with or disrupt the Service or its infrastructure</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Use the intercom system for harassment, threats, or fraudulent purposes</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Resell or redistribute the Service without written authorization</li>
            <li>Provide false or misleading tenant or property information</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Data Ownership & Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            You retain ownership of all data you input into the Service (property records, tenant
            information, designs, etc.). We process this data solely to provide the Service as
            described in our <a href="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</a>.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            <strong>We do not sell, share, or distribute your data to third parties.</strong> Your
            data will not be used for advertising, marketing, or any purpose unrelated to providing
            the Intrlock service.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            Property managers are responsible for ensuring they have appropriate consent to store
            tenant contact information within the platform.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Intercom & Communication Services</h2>
          <p className="text-gray-700 leading-relaxed">
            The video intercom feature connects visitors with tenants in real-time. Calls are not
            recorded or stored. SMS notifications may be sent to tenants when a visitor initiates a
            call. Standard messaging rates from your carrier may apply.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            Tenant contact information (phone numbers, emails) is never displayed to visitors.
            The system is designed to protect tenant privacy while enabling secure communication.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Access Control & Door Locks</h2>
          <p className="text-gray-700 leading-relaxed">
            Intrlock integrates with third-party access control hardware. While we strive for
            reliability, we do not guarantee uninterrupted operation of door lock systems. Property
            managers should maintain physical backup access methods. Intrlock is not liable for
            unauthorized entry resulting from hardware failures, network outages, or third-party
            system issues.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Sign Shop & Print Orders</h2>
          <p className="text-gray-700 leading-relaxed">
            The Sign Shop allows you to design and order printed products through our print partner
            (Printify). Orders are fulfilled by third-party print providers. Pricing, shipping times,
            and product quality are subject to the print provider’s terms. Intrlock facilitates the
            ordering process but is not the manufacturer or shipper.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Service Availability</h2>
          <p className="text-gray-700 leading-relaxed">
            We strive to maintain high availability but do not guarantee 100% uptime. The Service may
            be temporarily unavailable for maintenance, updates, or due to circumstances beyond our
            control. We will make reasonable efforts to notify users of planned maintenance in advance.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Limitation of Liability</h2>
          <p className="text-gray-700 leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, INTRLOCK SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA,
            OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE SERVICE.
          </p>
          <p className="text-gray-700 leading-relaxed mt-3">
            OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT
            YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">12. Indemnification</h2>
          <p className="text-gray-700 leading-relaxed">
            You agree to indemnify and hold harmless Intrlock and its officers, employees, and agents
            from any claims, damages, losses, or expenses (including attorney’s fees) arising from
            your use of the Service, your violation of these Terms, or your violation of any third-party
            rights.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">13. Termination</h2>
          <p className="text-gray-700 leading-relaxed">
            Either party may terminate the account at any time. We may suspend or terminate your access
            if you violate these Terms or engage in conduct that may harm the Service or other users.
            Upon termination, you may request an export of your data within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">14. Governing Law</h2>
          <p className="text-gray-700 leading-relaxed">
            These Terms are governed by the laws of the State of Maryland, United States, without regard
            to conflict of law principles. Any disputes shall be resolved in the state or federal courts
            located in Maryland.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">15. Changes to Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update these Terms from time to time. We will notify users of material changes via
            email or a notice on the platform at least 30 days before they take effect. Continued use
            after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">16. Contact</h2>
          <p className="text-gray-700 leading-relaxed">
            Questions about these Terms? Contact us at:
          </p>
          <p className="text-gray-700 mt-2">
            <strong>Intrlock</strong><br />
            Email: <a href="mailto:legal@intrlock.io" className="text-emerald-600 hover:underline">legal@intrlock.io</a><br />
            Website: <a href="https://intrlock.io" className="text-emerald-600 hover:underline">intrlock.io</a>
          </p>
        </section>
      </div>
    </div>
  </div>
);

export default Terms;
